// Per-scope idempotency key for completeSMBSignup. The scope is whatever
// identifier the caller uses to distinguish in-flight retries — currently
// the verified email; never a payment-method or session id.
//
// The scope is hashed before being used as a localStorage key suffix so
// abandoned-signup entries don't persist the raw email on shared devices.
// We use a non-cryptographic hash (cyrb53) because the goal is PII hygiene,
// not security: the hash hides the email from anyone inspecting
// localStorage but doesn't protect against a determined attacker. The
// idempotency value itself is a UUID and not derived from the scope.
const KEY_PREFIX = 'self-service-smb-signup:idem';

function cyrb53(input: string, seed = 0): string {
  let h1 = 0xdeadbeef ^ seed;
  let h2 = 0x41c6ce57 ^ seed;
  for (let i = 0; i < input.length; i++) {
    const ch = input.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507);
  h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507);
  h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  // 53-bit hash → 14 hex chars
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(16).padStart(14, '0');
}

function hashScope(scope: string): string {
  return cyrb53(scope.trim().toLowerCase());
}

function storageKey(scope: string): string {
  return `${KEY_PREFIX}:${hashScope(scope)}`;
}

export function getOrCreateSubscriptionIdempotencyKey(scope: string): string {
  const key = storageKey(scope);
  const existing = localStorage.getItem(key);
  if (existing) return existing;
  const fresh = crypto.randomUUID();
  localStorage.setItem(key, fresh);
  return fresh;
}

export function clearSubscriptionIdempotencyKey(scope: string): void {
  localStorage.removeItem(storageKey(scope));
}
